// src/components/common/Subscription/SubscriptionStatusBanner.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X, Calendar, ArrowRight } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useSubscriptionStatus } from '../../../hooks/useSubscriptionStatus';
import styles from './SubscriptionStatusBanner.module.css';

const SubscriptionStatusBanner = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const { shouldShowWarning, shouldShowError, daysRemaining, subscription } = useSubscriptionStatus();
  
  const [isDismissed, setIsDismissed] = useState(false);

  if (!shouldShowWarning && !shouldShowError) {
    return null;
  }

  if (isDismissed && shouldShowWarning) {
    return null;
  }

  const handleRenewClick = () => {
    navigate('/subscription/plans');
  };

  const handleDismiss = () => {
    if (shouldShowWarning) {
      setIsDismissed(true);
    }
  };

  const getBannerType = () => {
    if (shouldShowError) return 'error';
    if (shouldShowWarning) return 'warning';
    return 'info';
  };

  const getMessage = () => {
    if (subscription?.status === 'expired') {
      return t('subscription.subscriptionExpired');
    }
    if (subscription?.status === 'cancelled') {
      return t('subscription.subscriptionCancelled');
    }
    if (subscription?.status === 'suspended') {
      return t('subscription.subscriptionSuspended');
    }
    if (shouldShowWarning) {
      return t('subscription.subscriptionExpiringSoon', { days: daysRemaining });
    }
    return '';
  };

  const bannerType = getBannerType();
  const canDismiss = shouldShowWarning && !shouldShowError;

  return (
    <div className={`${styles.banner} ${styles[bannerType]}`}>
      <div className={styles.bannerContent}>
        <div className={styles.bannerLeft}>
          <AlertTriangle className={styles.icon} size={20} />
          <div className={styles.message}>
            <span className={styles.messageText}>{getMessage()}</span>
            {shouldShowWarning && daysRemaining !== null && (
              <span className={styles.daysRemaining}>
                <Calendar size={14} />
                {t('subscription.daysRemaining', { count: daysRemaining })}
              </span>
            )}
          </div>
        </div>

        <div className={styles.bannerRight}>
          <button
            className={styles.renewButton}
            onClick={handleRenewClick}
          >
            {t('subscription.renewNow')}
            <ArrowRight size={16} />
          </button>

          {canDismiss && (
            <button
              className={styles.dismissButton}
              onClick={handleDismiss}
              aria-label={t('common.dismiss')}
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionStatusBanner;